import moment, { Moment } from 'moment-timezone'
import { FC, SetStateAction, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocation } from 'react-use'
import { SorterResult } from 'antd/lib/table/interface'
import { PaginationConfig } from 'antd/lib/pagination'
import { RangeValue } from 'rc-picker/lib/interface'
import { Layout, Alert } from 'antd'
import { QUALITY_DEFAULT_PAGE } from '@OpsQuality/constants'
import {
    useBusinessUnitsData,
    useSalesDriData,
    useSchedulingProcessTypes,
} from '@ProdWb/logic'
import { ASSIGNED_TO_JOB, TOP_NAV_CONTENTS } from '@OpsWb/constants'
import styles from '@OpsQuality/styles.css'

import { OPERATIONS } from '~/redux-modules/preferences'
import { DEFAULT_REFRESH_MSG } from '~/lib/flashMsgs'
import { NavigationContext } from '~/core/NavigationConfig'

import {
    JobRequestSortMapping,
    JOB_REQUEST_LIST_PAGINATION_LIMIT,
} from './constants'
import { getAdminJobRequestsQuality_adminJobRequests_items as JobRequestType } from './components/__generated__/getAdminJobRequestsQuality'
import {
    useJobRequestListData,
    getUrlFilter,
    getFormattedFilters,
    RANGES_ARRAY,
    useMPCountries,
} from './components/logic'
import { UrlFilterType } from './types'
import FilterActions from './components/FilterActions'
import FiltersPanel from './components/FiltersPanel'

const ONE = 1

const getDefaultFromDate = () => {
    return moment().startOf('day')
}
const getDefaultToDate = () => {
    return moment().startOf('day').add(ONE, 'days')
}

const JobRequestList: FC = () => {
    const { setNavigationConfig } = useContext(NavigationContext)

    useEffect(() => {
        setNavigationConfig({
            sidebar: OPERATIONS,
            showOpsNav: true,
            navTitle: TOP_NAV_CONTENTS.QUALITY,
        })
    }, [])

    const location = useLocation()
    const navigate = useNavigate()

    const urlFilters: UrlFilterType = getUrlFilter(location)
    const {
        searchString,
        fromDate,
        toDate,
        statuses,
        priorityTypes,
        platformProcessTypeIds,
        businessUnits: businessUnitUrlFilter,
        inspectionStatus,
        mfgDriIds,
        payout,
        countryIds,
    } = urlFilters
    const formattedFilters = getFormattedFilters(urlFilters)
    const [sort, setSort] = useState<string | undefined>()
    const [paginationInfo, setPaginationInfo] = useState<PaginationConfig>()
    const [pageNumber, setPageNumber] = useState<number>(QUALITY_DEFAULT_PAGE)
    const [filterDrawerVisible, setFilterDrawerVisible] =
        useState<boolean>(false)
    const [pendingSearchType, setPendingSearchType] = useState<
        string | undefined
    >()
    const [pendingSearchString, setPendingSearchString] =
        useState<string>(searchString)
    const [pendingFromDate, setPendingFromDate] = useState<
        Moment | undefined | null
    >()
    const [pendingToDate, setPendingToDate] = useState<
        Moment | undefined | null
    >()
    const [pendingRange, setPendingRange] = useState<string | undefined>()
    const [pendingStatusFilters, setPendingStatusFilters] = useState<
        string[] | undefined | null
    >()
    const [pendingPriorityFilters, setPendingPriorityFilters] = useState<
        string[] | undefined | null
    >()
    const [pendingProcessTypeFilters, setPendingProcessTypeFilters] = useState<
        string[] | undefined | null
    >()
    const [pendingBusinessUnitFilters, setPendingBusinessUnitFilters] =
        useState<string[] | undefined | null>()
    const [mfgDriFilter, setMfgDriFilter] = useState<
        string[] | undefined | null
    >()
    const [inspectionStatusFilter, setInspectionStatusFilter] = useState<
        string[] | undefined | null
    >()
    const [payoutFilter, setPayoutFilter] = useState<
        string[] | undefined | null
    >()
    const [countryFilters, setCountryFilters] = useState<
        string[] | undefined | null
    >()
    const onChange = (page: SetStateAction<number>) => {
        setPageNumber(page as number)
    }
    const { data, loading, error } = useJobRequestListData(
        JOB_REQUEST_LIST_PAGINATION_LIMIT,
        formattedFilters,
        (pageNumber - 1) * JOB_REQUEST_LIST_PAGINATION_LIMIT,
        sort || JobRequestSortMapping.vendorDueDate_descend,
    )
    const { data: processTypes } = useSchedulingProcessTypes()
    const { data: salesDriData } = useSalesDriData(ASSIGNED_TO_JOB)
    const mpCountries = useMPCountries()
    const { data: businessUnits } = useBusinessUnitsData()

    const onDateChange = (
        _dates: RangeValue<moment.Moment>,
        [selectedFromDateString, selectedToDateString]: [string, string],
    ) => {
        if (!selectedFromDateString && !selectedToDateString) {
            setPendingFromDate(null)
            setPendingToDate(null)
            return
        }

        const selectedFromDate = new Date(selectedFromDateString)
        const selectedToDate = new Date(selectedToDateString)
        const selectedRange = RANGES_ARRAY.find(eachRange => {
            return (
                eachRange.val?.[0] === moment(selectedFromDate) &&
                eachRange.val?.[1] === moment(selectedToDate)
            )
        })

        setPendingRange(selectedRange?.key || undefined)
        setPendingFromDate(moment(selectedFromDate) || undefined)
        setPendingToDate(moment(selectedToDate) || undefined)
        setPendingSearchString('')
    }
    const createFilterUrl = ({
        remainingPriorityFilters,
        remainingStatusFilters,
        remainingProcessTypeFilters,
        remainingFromDate,
        remainingToDate,
        remainingRange,
        remainingSearchString,
        remainingSearchType,
        remainingMfgDriFilter,
        remainingInspectionStatusFilter,
        remainingPayoutFilter,
        remainingCountryFilters,
        remainingBusinessUnitFilters,
    }: {
        remainingPriorityFilters?: string[] | null
        remainingStatusFilters?: string[] | null
        remainingProcessTypeFilters?: string[] | null
        remainingFromDate?: Moment | null
        remainingToDate?: Moment | null
        remainingRange?: string
        remainingSearchString?: string
        remainingSearchType?: string
        remainingMfgDriFilter?: string[] | null
        remainingInspectionStatusFilter?: string[] | null
        remainingPayoutFilter?: (string | number)[] | null
        remainingCountryFilters?: string[] | null
        remainingBusinessUnitFilters?: string[] | null
    }) => {
        const filterArray = []

        if (remainingSearchString) {
            filterArray.push(
                `searchType=${
                    remainingSearchType || ''
                }|searchString=${remainingSearchString}`,
            )
            return navigate(
                `/admin/app/operations/quality/?filters=${filterArray.join(
                    '|',
                )}`,
            )
        }

        if (remainingRange) {
            filterArray.push(`range=${remainingRange}`)
        } else {
            /* eslint-disable no-nested-ternary */
            let from, to

            if (remainingFromDate === undefined) {
                from =
                    fromDate !== undefined
                        ? fromDate
                            ? fromDate.format('MMDDYYYY')
                            : ''
                        : getDefaultFromDate().format('MMDDYYYY')
            } else {
                from = remainingFromDate
                    ? moment(remainingFromDate).format('MMDDYYYY')
                    : ''
            }

            if (remainingToDate === undefined) {
                to =
                    toDate !== undefined
                        ? toDate
                            ? toDate.format('MMDDYYYY')
                            : ''
                        : getDefaultToDate().format('MMDDYYYY')
            } else {
                to = remainingToDate
                    ? moment(remainingToDate).format('MMDDYYYY')
                    : ''
            }

            filterArray.push(`fromDate=${from}`)
            filterArray.push(`toDate=${to}`)
        }

        if (remainingPriorityFilters?.length) {
            filterArray.push(
                `priorityTypes=${remainingPriorityFilters.join(',')}`,
            )
        } else if (
            priorityTypes &&
            priorityTypes.length &&
            !remainingPriorityFilters
        ) {
            filterArray.push(`priorityTypes=${priorityTypes.join(',')}`)
        }

        if (remainingBusinessUnitFilters?.length) {
            filterArray.push(
                `businessUnits=${remainingBusinessUnitFilters.join(',')}`,
            )
        } else if (
            businessUnitUrlFilter &&
            businessUnitUrlFilter.length &&
            !remainingBusinessUnitFilters
        ) {
            filterArray.push(`businessUnits=${businessUnitUrlFilter.join(',')}`)
        }

        if (remainingProcessTypeFilters?.length) {
            filterArray.push(
                `platformProcessTypeIds=${remainingProcessTypeFilters.join(
                    ',',
                )}`,
            )
        } else if (
            platformProcessTypeIds &&
            platformProcessTypeIds.length &&
            !remainingProcessTypeFilters
        ) {
            filterArray.push(
                `platformProcessTypeIds=${platformProcessTypeIds.join(',')}`,
            )
        }

        if (remainingStatusFilters?.length) {
            filterArray.push(`statuses=${remainingStatusFilters.join(',')}`)
        } else if (statuses && statuses.length && !remainingStatusFilters) {
            filterArray.push(`statuses=${statuses.join(',')}`)
        }

        if (remainingMfgDriFilter?.length) {
            filterArray.push(`mfgDriIds=${remainingMfgDriFilter.join(',')}`)
        } else if (mfgDriIds && mfgDriIds.length && !remainingMfgDriFilter) {
            filterArray.push(`mfgDriIds=${mfgDriIds.join(',')}`)
        }

        if (remainingInspectionStatusFilter?.length) {
            filterArray.push(
                `inspectionStatus=${remainingInspectionStatusFilter.join(',')}`,
            )
        } else if (
            inspectionStatus &&
            inspectionStatus.length &&
            !remainingInspectionStatusFilter
        ) {
            filterArray.push(`inspectionStatus=${inspectionStatus.join(',')}`)
        }

        if (remainingPayoutFilter?.length) {
            filterArray.push(`payout=${remainingPayoutFilter.join(',')}`)
        } else if (payout && payout.length && !remainingPayoutFilter) {
            filterArray.push(`payout=${payout.join(',')}`)
        }

        if (remainingCountryFilters?.length) {
            filterArray.push(`countryIds=${remainingCountryFilters.join(',')}`)
        } else if (
            countryIds &&
            countryIds.length &&
            !remainingCountryFilters
        ) {
            filterArray.push(`countryIds=${countryIds.join(',')}`)
        }

        return navigate(
            `/admin/app/operations/quality/?filters=${filterArray.join('|')}`,
        )
    }
    const toggleFilters = () => {
        setFilterDrawerVisible(!filterDrawerVisible)
    }
    const handleTableSorterChange = (
        sorter: SorterResult<JobRequestType> | SorterResult<JobRequestType>[],
    ) => {
        const key = `${(sorter as SorterResult<JobRequestType>).columnKey}_${
            (sorter as SorterResult<JobRequestType>).order
        }`

        setSort(
            (sorter as SorterResult<JobRequestType>).order
                ? JobRequestSortMapping[key]
                : undefined,
        )
    }
    const onCloseTag = (selectedFilters: string[], filterTitle: string) => {
        if (filterTitle === 'Country') {
            createFilterUrl({
                remainingCountryFilters: selectedFilters,
            })
            setCountryFilters(selectedFilters)
        } else if (filterTitle === 'Priority') {
            createFilterUrl({
                remainingPriorityFilters: selectedFilters,
            })
            setPendingPriorityFilters(selectedFilters)
        } else if (filterTitle === 'DRI') {
            createFilterUrl({
                remainingMfgDriFilter: selectedFilters,
            })
            setMfgDriFilter(selectedFilters)
        } else if (filterTitle === 'Process type') {
            createFilterUrl({
                remainingProcessTypeFilters: selectedFilters,
            })
            setPendingProcessTypeFilters(selectedFilters)
        } else if (filterTitle === 'Business unit') {
            createFilterUrl({
                remainingBusinessUnitFilters: selectedFilters,
            })
            setPendingBusinessUnitFilters(selectedFilters)
        }
    }
    const onFilterChange = (
        pagination: PaginationConfig,
        sorter: SorterResult<JobRequestType>,
    ) => {
        const key = `${sorter.columnKey}_${sorter.order}`

        setSort(sorter.order ? JobRequestSortMapping[key] : undefined)
        setPaginationInfo(pagination)
    }
    const clearFilters = () => {
        setPendingFromDate(null)
        setPendingToDate(null)
        setPendingStatusFilters(undefined)
        setPendingPriorityFilters(undefined)
        setPendingProcessTypeFilters(undefined)
        setFilterDrawerVisible(false)
        setInspectionStatusFilter(undefined)
        setPayoutFilter(undefined)
        setMfgDriFilter(undefined)
        setCountryFilters(undefined)
        setPendingBusinessUnitFilters(undefined)

        return navigate(
            '/admin/app/operations/quality/?filters=fromDate=|toDate=',
        )
    }
    const onEnterSearch = (value: string, remainingSearchType: string) => {
        setPageNumber(QUALITY_DEFAULT_PAGE)
        createFilterUrl({
            remainingSearchString: value,
            remainingSearchType,
        })
    }
    const applyFilters = () => {
        setPageNumber(QUALITY_DEFAULT_PAGE)
        if (pendingRange) {
            createFilterUrl({
                remainingRange: pendingRange,
                remainingPriorityFilters: pendingPriorityFilters,
                remainingProcessTypeFilters: pendingProcessTypeFilters,
                remainingBusinessUnitFilters: pendingBusinessUnitFilters,
                remainingStatusFilters: pendingStatusFilters,
                remainingMfgDriFilter: mfgDriFilter,
                remainingInspectionStatusFilter: inspectionStatusFilter,
                remainingPayoutFilter: payoutFilter,
                remainingCountryFilters: countryFilters,
            })
        } else {
            let from, to

            if (pendingFromDate === undefined) {
                from = fromDate !== undefined ? fromDate : getDefaultFromDate()
            } else {
                from = pendingFromDate
            }

            if (pendingToDate === undefined) {
                to = toDate !== undefined ? toDate : getDefaultToDate()
            } else {
                to = pendingToDate
            }
            createFilterUrl({
                remainingFromDate: from,
                remainingToDate: to,
                remainingPriorityFilters: pendingPriorityFilters,
                remainingProcessTypeFilters: pendingProcessTypeFilters,
                remainingBusinessUnitFilters: pendingBusinessUnitFilters,
                remainingStatusFilters: pendingStatusFilters,
                remainingMfgDriFilter: mfgDriFilter,
                remainingInspectionStatusFilter: inspectionStatusFilter,
                remainingPayoutFilter: payoutFilter,
                remainingCountryFilters: countryFilters,
            })
        }

        setFilterDrawerVisible(false)
        setPendingSearchString('')
    }

    if (error) {
        return <Alert message={DEFAULT_REFRESH_MSG} type="error" showIcon />
    }

    return (
        <Layout.Content className={styles.layoutList}>
            <FilterActions
                toggleFilters={toggleFilters}
                pendingSearchType={pendingSearchType}
                pendingSearchString={pendingSearchString}
                onEnterSearch={onEnterSearch}
                setPendingSearchString={setPendingSearchString}
                setPendingSearchType={setPendingSearchType}
                setPendingFromDate={setPendingFromDate}
                createFilterUrl={createFilterUrl}
                getDefaultFromDate={getDefaultFromDate}
                getDefaultToDate={getDefaultToDate}
                setPendingToDate={setPendingToDate}
                setPendingPriorityFilters={setPendingPriorityFilters}
                setPayoutFilter={setPayoutFilter}
                setPendingStatusFilters={setPendingStatusFilters}
                setInspectionStatusFilter={setInspectionStatusFilter}
                onCloseTag={onCloseTag}
                processTypes={processTypes}
                salesDriData={salesDriData}
                mpCountries={mpCountries}
                businessUnits={businessUnits}
            />
            <FiltersPanel
                data={data}
                loading={loading}
                handleTableSorterChange={handleTableSorterChange}
                paginationInfo={paginationInfo}
                onChange={onChange}
                pageNumber={pageNumber}
                sort={sort}
                onFilterChange={onFilterChange}
                setFilterDrawerVisible={setFilterDrawerVisible}
                filterDrawerVisible={filterDrawerVisible}
                pendingFromDate={pendingFromDate}
                pendingToDate={pendingToDate}
                onDateChange={onDateChange}
                pendingStatusFilters={pendingStatusFilters}
                setPendingStatusFilters={setPendingStatusFilters}
                pendingPriorityFilters={pendingPriorityFilters}
                setPendingPriorityFilters={setPendingPriorityFilters}
                pendingProcessTypeFilters={pendingProcessTypeFilters}
                setPendingProcessTypeFilters={setPendingProcessTypeFilters}
                pendingBusinessUnitFilters={pendingBusinessUnitFilters}
                setPendingBusinessUnitFilters={setPendingBusinessUnitFilters}
                inspectionStatusFilter={inspectionStatusFilter}
                setInspectionStatusFilter={setInspectionStatusFilter}
                payoutFilter={payoutFilter}
                setPayoutFilter={setPayoutFilter}
                mfgDriFilter={mfgDriFilter}
                setMfgDriFilter={setMfgDriFilter}
                countryFilters={countryFilters}
                setCountryFilters={setCountryFilters}
                applyFilters={applyFilters}
                clearFilters={clearFilters}
                processTypes={processTypes}
                salesDriData={salesDriData}
                mpCountries={mpCountries}
                businessUnits={businessUnits}
            />
        </Layout.Content>
    )
}

export default JobRequestList
